import { Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { HUD } from '@/config/hud';
import { MAX_ACTIVE_WEAPONS, WEAPONS } from '@/config/weapons';
import { WEAPON_CATEGORY_COLOR, weaponGlyphKey } from '@/config/weapon-visuals';
import { getTexture } from '@/assets/resolver';
import type { WeaponHolder } from '@/entities/weapons';
import { bagGlyph, padlockGlyph, plusGlyph, sparkleGlyph, swapGlyph } from './hangar-icons';
import { PickupToast } from './hud-pickup';
import type { CrateDestination } from './shop-types';
import { chamferPanel, hexPath, UI_FONT } from './widgets';

const W = HUD.weapons;
const BAR_X = 7;
const BAR_W = W.chipW - BAR_X * 2;

interface Chip {
  view: Container;
  frame: Graphics;
  icon: Sprite;
  num: Text;
  marks: Graphics;
  reload: Graphics;
}

/** Bag read-out for the strip: spares carried, slots, new ones, waiting crate. */
export interface BagView {
  count: number;
  cap: number;
  fresh: number;
  crate: boolean;
}

function chipText(size: number, fill: number): Text {
  const text = new Text({
    text: '',
    style: new TextStyle({ fontFamily: UI_FONT, fontSize: size, fill, letterSpacing: 1 }),
  });
  text.anchor.set(0.5);
  return text;
}

/**
 * HUD weapon strip (docs/06, docs/09 wave 4): sockets 1-2-3 — the SAME
 * numbered, category-outlined tiles as the shop — then the BAG chip
 * (count, "new" sparkle, swap mark when a crate waits). Wordless: glyph,
 * number, colour. Repaints only on change; per frame the reload bars and
 * the sparkle move, and the pickup toast animates.
 */
export class WeaponStrip {
  readonly view = new Container();
  private readonly chips: Chip[] = [];
  private readonly ids: Array<string | null> = [null, null, null];
  private capacity = -1;
  private readonly bag: Chip;
  private readonly spark = new Graphics();
  /** Last painted bag read-out (field compare — no per-frame strings) */
  private readonly shown: BagView = { count: -1, cap: -1, fresh: -1, crate: false };
  private time = 0;
  private readonly toast = new PickupToast();

  constructor() {
    for (let i = 0; i <= MAX_ACTIVE_WEAPONS; i++) {
      const view = new Container();
      view.x = i * (W.chipW + W.gap);
      const frame = new Graphics();
      const icon = new Sprite(Texture.EMPTY);
      icon.anchor.set(0.5);
      icon.scale.set(W.iconScale);
      // Nudged off the number badge (top-left)
      icon.position.set(W.chipW / 2 + 5, W.chipH / 2 + 1);
      const num = chipText(W.badgeSize, 0x0f0c1a);
      const marks = new Graphics();
      const reload = new Graphics();
      reload.roundRect(0, 0, BAR_W, W.reloadBarH, W.reloadBarH / 2).fill(0xffffff);
      reload.position.set(BAR_X, W.chipH - W.reloadBarH - 5);
      view.addChild(frame, icon, marks, num, reload);
      this.view.addChild(view);
      this.chips.push({ view, frame, icon, num, marks, reload });
    }
    this.bag = this.chips.pop()!;
    this.bag.icon.visible = this.bag.reload.visible = false;
    this.bag.num.style.fill = 0xe8eaf0;
    this.bag.num.position.set(W.chipW / 2, W.chipH - 14);
    this.bag.view.addChild(this.spark);
    this.view.addChild(this.toast.view);
  }

  /** Per frame — cheap unless the loadout or the bag changed. */
  sync(holder: WeaponHolder, bag: BagView): void {
    const cap = holder.capacity;
    let dirty = cap !== this.capacity;
    for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
      if ((holder.equipped[i] ?? null) !== this.ids[i]) dirty = true;
    }
    if (dirty) {
      this.capacity = cap;
      for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
        this.ids[i] = holder.equipped[i] ?? null;
        this.paint(i, this.ids[i] ?? null, i < cap);
      }
    }
    const last = this.shown;
    if (bag.count !== last.count || bag.cap !== last.cap || bag.fresh !== last.fresh || bag.crate !== last.crate) {
      last.count = bag.count;
      last.cap = bag.cap;
      last.fresh = bag.fresh;
      last.crate = bag.crate;
      this.paintBag(bag);
    }
    for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
      const chip = this.chips[i]!;
      const id = this.ids[i];
      if (!id || !chip.reload.visible) continue;
      // Fast guns would just flicker — they show a steady "ready" bar
      const slow = holder.reloadPeriod(i) >= W.minReloadShown;
      const pct = slow ? holder.reloadPct(i) : 1;
      chip.reload.scale.x = Math.max(0.04, pct);
      chip.reload.alpha = pct >= 1 ? 1 : 0.5;
    }
  }

  /** Frame tick: sparkle pulse + toast animation. */
  update(dt: number): void {
    this.time += dt;
    if (this.spark.visible) this.spark.scale.set(0.8 + 0.3 * Math.abs(Math.sin(this.time * W.sparklePulse)));
    this.toast.update(dt);
  }

  showPickup(weaponId: string, dest: CrateDestination, socket: number): void {
    this.toast.show(weaponId, dest, socket);
  }

  private paint(i: number, weaponId: string | null, open: boolean): void {
    const chip = this.chips[i]!;
    const def = weaponId ? WEAPONS[weaponId] : undefined;
    const f = chip.frame.clear();
    const m = chip.marks.clear();
    chip.view.alpha = open ? 1 : W.lockedAlpha;
    chip.icon.visible = chip.reload.visible = def !== undefined;
    const color = def ? WEAPON_CATEGORY_COLOR[def.category] : 0x3a3548;
    chamferPanel(f, W.chipW, W.chipH, {
      fill: def ? 0x14101f : 0x110e1b, fillAlpha: def ? 0.88 : 0.75,
      stroke: color, strokeWidth: def ? 3 : 2, notch: 9, plain: true,
    });
    // Number badge — the socket's identity, same as the shop tile
    hexPath(f, W.badgeR + 2, W.badgeR + 2, W.badgeR).fill(open ? (def ? color : 0x5a6168) : 0x3a3548);
    chip.num.text = String(i + 1);
    chip.num.position.set(W.badgeR + 2, W.badgeR + 3);
    if (!open) padlockGlyph(m, W.chipW / 2 + 4, W.chipH / 2 + 4, 11, 0x5a6168);
    else if (!def) plusGlyph(m, W.chipW / 2 + 4, W.chipH / 2 + 3, 9, 0x4a4256);
    if (!def) return;
    chip.icon.texture = getTexture(weaponGlyphKey(def.id));
    chip.reload.tint = color;
  }

  private paintBag(bag: BagView): void {
    const chip = this.bag;
    const full = bag.count >= bag.cap;
    const stroke = bag.crate ? COLORS.dangerOrange : bag.fresh > 0 ? COLORS.playerYellow : 0x4a4256;
    const f = chip.frame.clear();
    chamferPanel(f, W.chipW, W.chipH, {
      fill: 0x14101f, fillAlpha: 0.85, stroke, strokeWidth: bag.crate || bag.fresh > 0 ? 3 : 2,
      notch: 9, plain: true,
    });
    const m = chip.marks.clear();
    bagGlyph(m, W.chipW / 2, W.chipH / 2 - 8, W.bagGlyph, bag.crate ? COLORS.dangerOrange : COLORS.playerCream);
    if (bag.crate) swapGlyph(m, W.chipW - 13, 13, 7, COLORS.playerYellow);
    chip.num.text = `${bag.count}/${bag.cap}`;
    chip.num.style.fill = full ? COLORS.dangerOrange : 0xe8eaf0;
    const s = this.spark.clear();
    s.visible = bag.fresh > 0 && !bag.crate;
    sparkleGlyph(s, 0, 0, 7, COLORS.playerYellow);
    s.position.set(W.chipW - 13, 13);
  }
}
